/**
 * analyticsApi.js
 * Calls the BotBridge Django backend for campaign analytics data.
 * Base URL is read from REACT_APP_API_BASE_URL env var.
 */

// const API_BASE_URL = "http://localhost:8000" || "";
import { API_BASE_URL } from "../../config/urls";

/**
 * fetchCampaignAnalytics
 * Calls GET /api/ctwa/analytics/campaigns/
 *
 * @param {object} params
 * @param {string} params.adAccountId   - Meta ad account ID e.g. "act_123456"
 * @param {string} params.datePreset    - e.g. "last_7d"
 * @param {string} [params.since]       - "YYYY-MM-DD" — overrides datePreset
 * @param {string} [params.until]       - "YYYY-MM-DD" — overrides datePreset
 * @returns {Promise<object>}           - { campaigns, totals, meta }
 */
export async function fetchCampaignAnalytics({
  // adAccountId,
  datePreset = "last_7d",
  since,
  until,
}) {
  const params = new URLSearchParams();

  if (since && until) {
    params.set("since", since);
    params.set("until", until);
  } else {
    params.set("date_preset", datePreset);
  }

  const token = localStorage.getItem("token");
  const tenant = localStorage.getItem("tenant");

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": tenant,
  };

  console.log(headers);

  const response = await fetch(
    `${API_BASE_URL}/ctwa/analytics/campaigns/?${params}`,
    {
      method: "GET",
      headers,
    },
  );

  if (!response.ok) {
    let msg = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      msg = err.error || msg;
    } catch (_) {}
    throw new Error(msg);
  }

  return response.json();
}

/**
 * fetchSingleCampaignAnalytics
 * Calls GET /api/ctwa/analytics/campaigns/<campaign_id>/
 *
 * @param {object} params
 * @param {string} params.campaignId  - Meta campaign ID e.g. "120215..."
 * @param {string} params.datePreset  - e.g. "last_7d"
 * @param {string} [params.since]
 * @param {string} [params.until]
 * @returns {Promise<object>}         - { campaign_id, campaign_name, metrics, ad_sets, meta }
 */
export async function fetchSingleCampaignAnalytics({
  campaignId,
  datePreset = "last_7d",
  since,
  until,
}) {
  const params = new URLSearchParams();
  if (since && until) {
    params.set("since", since);
    params.set("until", until);
  } else {
    params.set("date_preset", datePreset);
  }

  const token = localStorage.getItem("token");
  const tenant = localStorage.getItem("tenant");

  const response = await fetch(
    `${API_BASE_URL}/ctwa/analytics/campaigns/${campaignId}/?${params}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      },
    },
  );

  if (!response.ok) {
    let msg = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      msg = err.error || msg;
    } catch (_) {}
    throw new Error(msg);
  }

  return response.json();
}

/**
 * exportAnalyticsCSV
 * Converts the hierarchical campaign data to a flat CSV and triggers a download.
 *
 * @param {Array}  campaigns
 * @param {string} currency
 */
export function exportAnalyticsCSV(campaigns, currency = "INR") {
  const fmt = (v) => (v === null || v === undefined ? "" : v);
  const fmtCurr = (v) =>
    v === null || v === undefined
      ? ""
      : new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(v);

  const rows = [
    [
      "Level",
      "Campaign Name",
      "Ad Set Name",
      "Account Name",
      "Ad Name",
      "Delivery Status",
      "Reach",
      "Impressions",
      "Clicks (all)",
      "CPC (all)",
      "Amount Spent",
      "Conversations",
      "Schedule",
    ],
  ];

  campaigns.forEach((c) => {
    // Campaign level
    rows.push([
      "Campaign",
      c.campaign_name,
      "",
      "",
      "",
      c.delivery_label,
      fmt(c.metrics?.reach),
      fmt(c.metrics?.impressions),
      fmt(c.metrics?.clicks),
      fmtCurr(c.metrics?.cpc),
      fmtCurr(c.metrics?.spend),
      fmt(c.metrics?.conversations),
      "",
    ]);

    c.ad_sets?.forEach((s) => {
      rows.push([
        "Ad Set",
        c.campaign_name,
        s.adset_name,
        "",
        "",
        s.delivery_label,
        fmt(s.metrics?.reach),
        fmt(s.metrics?.impressions),
        fmt(s.metrics?.clicks),
        fmtCurr(s.metrics?.cpc),
        fmtCurr(s.metrics?.spend),
        fmt(s.metrics?.conversations),
        "",
      ]);

      s.accounts?.forEach((a) => {
        a.ads?.forEach((ad) => {
          rows.push([
            "Ad",
            c.campaign_name,
            s.adset_name,
            a.account_name,
            ad.ad_name,
            ad.delivery_label,
            fmt(ad.metrics?.reach),
            fmt(ad.metrics?.impressions),
            fmt(ad.metrics?.clicks),
            fmtCurr(ad.metrics?.cpc),
            fmtCurr(ad.metrics?.spend),
            fmt(ad.metrics?.conversations),
            ad.schedule || "",
          ]);
        });
      });
    });
  });

  const csv = rows
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `ctwa_campaign_analytics_${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
